import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { patchSearchParams } from './useUrlState';

/**
 * Tab / sub-view state kept in the URL instead of component state.
 *
 * Why this exists: a plain `useState` tab is lost the moment you navigate away and come back. Open
 * Payments → "By bank" → a bank's statement → click a name → press Back, and you land on the "Payments"
 * tab, not the one you left. The page looks like it forgot where you were, and any drill-down under the
 * tab is gone with it. Putting the tab in the query string makes the browser's own history entry carry
 * it, so Back restores the exact view — and the URL becomes shareable/bookmarkable for free.
 *
 * Switching tabs REPLACES the history entry rather than pushing one, so Back still leaves the page
 * (one press) instead of walking backwards through every tab the user tried. A tab change and a
 * filter change made in the SAME handler both survive — see the note on the setter.
 *
 * The fallback value is never written to the URL, keeping the default view's address clean.
 */
export function useUrlTab<T extends string>(
  key: string,
  valid: readonly T[],
  fallback: T,
  // The setter takes a plain `string` so it can be handed straight to `<Tabs onChange>` (which is typed
  // `(key: string) => void`); an unrecognised value falls back rather than writing junk to the URL.
): [T, (v: string) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  // An unknown value (hand-edited URL, a tab that no longer exists) degrades to the default rather
  // than rendering nothing.
  const value = raw && (valid as readonly string[]).includes(raw) ? (raw as T) : fallback;

  /*
   * Written through the SHARED buffer in useUrlState, not with a snapshot of `params` — for the
   * reason spelled out on `useUrlParam` below, which this hook used to be the last holdout from.
   *
   * A handler that moves the reader to another tab AND sets the filter that makes the move
   * meaningful is the normal shape of a drill-down: Coverage's "See this person's customers" does
   * `setRepId(id); setGrain('customer')`. Off two stale snapshots those two writes raced, the
   * buffered one landed last, and the URL kept `grain=rep&rep=5` — the filter applied, the tab
   * never moved, and the button read as dead because the grain it filtered was not on screen.
   */
  const set = useCallback(
    (v: string) => {
      const picked = (valid as readonly string[]).includes(v) ? v : fallback;
      patchSearchParams(
        (buf) => { if (picked === fallback) buf.delete(key); else buf.set(key, picked); },
        (final) => setParams(final, { replace: true }),
      );
    },
    [setParams, key, fallback],
  );

  return [value, set];
}

/**
 * The same idea for a free-form selection under a tab (a picked bank account, an expanded row) — any
 * value is accepted, and clearing it drops the param. Keeps a drill-down alive across a round trip to
 * a detail page.
 */
export function useUrlParam(key: string): [string | undefined, (v: string | undefined) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? undefined;

  /*
   * Written through the SHARED buffer in useUrlState, not with a snapshot of `params`.
   *
   * A handler that changes this AND a `useUrlState` filter had two writes racing off the same stale
   * snapshot, and the second silently overwrote the first. On the By-bank page that meant the
   * "Accounts reconciled" tile — which narrows the table and closes any open register in one
   * handler — kept `?bank=NONE`, so it narrowed the accounts while leaving the previous tile's
   * register open below: two filters from two tiles, neither announced.
   */
  const set = useCallback(
    (v: string | undefined) => {
      patchSearchParams(
        (buf) => { if (v == null || v === '') buf.delete(key); else buf.set(key, v); },
        (final) => setParams(final, { replace: true }),
      );
    },
    [setParams, key],
  );

  return [value, set];
}
